import { useContentfulSingingPortraitSongs } from 'api/contentful/memory'
import {
  modalContentCss,
  modalCss,
  SButton,
  SSongListWrapper,
  STitle,
  STitleRow,
} from './ChooseSongModal.styled'
import { SongListItem } from 'components/content/singingPortraits/chooseSongModal/SongListItem'
import { Icon } from 'components/icon'
import { AnimationDriver } from 'components/memory/MemoryUtils'
import { ModalConfig } from 'components/modal/Modal'
import {
  PromisingModalProps,
  usePromisingModal,
} from 'components/modal/promisingModal/PromisingModal'
import { BlockSpinner } from 'components/spinner/Spinner'

import { StateSpinner } from 'components/StateSpinner'
import { toastError } from 'components/Toast'
import { useEffect, useState } from 'react'
import { fireEvent } from 'utils/analytics'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'

const modalConfig: ModalConfig = { modalCss, modalContentCss }

const ChooseSongModal = (props: PromisingModalProps<AnimationDriver>) => {
  const { t, isLoading } = useDynamicTranslations('life_stories')
  const [selectedSongIndex, setSelectedSongIndex] = useState<number>(-1)

  const songsSwr = useContentfulSingingPortraitSongs()
  const selectedSong = songsSwr.data?.[selectedSongIndex]

  const onContinue = async () => {
    if (selectedSongIndex === -1) {
      toastError('choose_a_song')
      return
    }

    fireEvent('sh_song_selected', { song_name: selectedSong?.songName as string })

    await props.onResolve?.((selectedSong?.driverId as AnimationDriver) || null)
    await props.close()
  }

  if (isLoading) return <BlockSpinner />
  return (
    <div>
      <STitleRow>
        <Icon name="SelectSong" />
        <STitle>{t('life_stories.inside_page.choose_song_modal.title')}</STitle>
      </STitleRow>
      <StateSpinner isLoading={songsSwr.isValidating}>
        <SSongListWrapper>
          {songsSwr.data?.map((song, index) => (
            <SongListItem
              key={song?.driverId}
              songData={song}
              selected={selectedSongIndex === index}
              onSelect={() => setSelectedSongIndex(index)}
            />
          ))}
        </SSongListWrapper>
      </StateSpinner>
      <SButton type="button" size="large" onClick={onContinue}>
        {t('life_stories.inside_page.choose_song_modal.action_button')}
      </SButton>
    </div>
  )
}

export const useChooseSongModal = () => {
  const [modal, openModalAndPromise, closeModal, isOpen] = usePromisingModal<AnimationDriver>(
    ({ close, onResolve }) => <ChooseSongModal close={close} onResolve={onResolve} />,
    modalConfig
  )

  useEffect(() => {
    let temporarilyMutedElements: (HTMLVideoElement | HTMLAudioElement)[] = []

    if (isOpen) {
      const otherAudioPlayers = document.querySelectorAll('video, audio') as NodeListOf<
        HTMLVideoElement | HTMLAudioElement
      >
      otherAudioPlayers.forEach((p) => {
        if (!p.muted && !p.paused) {
          p.muted = true
          temporarilyMutedElements.push(p)
        }
      })
    }

    return () => {
      temporarilyMutedElements.forEach((e) => (e.muted = false))
      temporarilyMutedElements = []
    }
  }, [isOpen])

  return [modal, openModalAndPromise, closeModal, isOpen] as const
}
